import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faDownload,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faPaperclip
} from '@fortawesome/free-solid-svg-icons';
import { APIRoutes } from '@/services/api.routes';
import { MimeTypeIcon, type MimeType } from '@/components/ui/MimeTypeIcon';
import { ExpandableText } from '@/components/ui/ExpandableText';
import type { VideoID, VideoAttachment, VideoAttachmentID } from '@/models';
import EditVideoAttachmentModal from '@/components/attachments/EditVideoAttachmentModal';
import UploadVideoAttachmentModal from '@/components/attachments/UploadVideoAttachmentModal';

interface AdminVideoAttachmentsProps {
  videoId: VideoID;
}

export const AdminVideoAttachments: React.FC<AdminVideoAttachmentsProps> = ({ videoId }) => {
  const [attachments, setAttachments] = useState<VideoAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<VideoAttachment | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchAttachments();
  }, [videoId]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await APIRoutes.listVideoAttachments(videoId, {
        sort: 'order',
        sortDirection: 'ASC'
      });
      setAttachments(response.attachments);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError('Fehler beim Laden der Anhänge');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment: VideoAttachment) => {
    try {
      const blob = await APIRoutes.downloadVideoAttachment(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Fehler beim Herunterladen der Datei');
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!confirm('Möchten Sie diesen Anhang wirklich löschen?')) return;

    try {
      setDeletingIds(prev => new Set(prev).add(attachmentId));
      await APIRoutes.deleteVideoAttachment(attachmentId as VideoAttachmentID);
      await fetchAttachments();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Fehler beim Löschen des Anhangs');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachmentId);
        return newSet;
      });
    }
  };

  const handleEdit = (attachment: VideoAttachment) => {
    setSelectedAttachment(attachment);
    setEditModalOpen(true);
  };

  const handleUploadSuccess = () => {
    setUploadModalOpen(false);
    fetchAttachments();
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setSelectedAttachment(null);
    fetchAttachments();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-600 pt-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer"
        >
          <FontAwesomeIcon icon={faPaperclip} className="w-4 h-4" />
          <span>Anhänge ({attachments.length})</span>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="w-3 h-3"
          />
        </button>

        <button
          onClick={() => setUploadModalOpen(true)}
          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer"
          title="Anhang hinzufügen"
        >
          <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-xs">
          {error}
        </div>
      )}

      {/* Expanded Content */}
      {expanded && (
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-gray-400 animate-spin" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Lade Anhänge...</p>
            </div>
          ) : attachments.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Keine Anhänge vorhanden</p>
            </div>
          ) : (
            attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs"
              >
                <MimeTypeIcon
                  mimeType={attachment.blob.mimeType as MimeType}
                  className="w-4 h-4"
                />

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {attachment.filename}
                  </div>
                  {attachment.description !== null && attachment.description !== '' && (
                    <ExpandableText
                      text={attachment.description}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  )}
                  <div className="text-gray-400 dark:text-gray-500">
                    {formatFileSize(attachment.blob.fileSizeBytes)}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleDownload(attachment)}
                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                    title="Herunterladen"
                  >
                    <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                  </button>

                  <button
                    onClick={() => handleEdit(attachment)}
                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors cursor-pointer"
                    title="Bearbeiten"
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                  </button>

                  <button
                    onClick={() => handleDelete(attachment.id)}
                    disabled={deletingIds.has(attachment.id)}
                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer disabled:opacity-50"
                    title="Löschen"
                  >
                    <FontAwesomeIcon
                      icon={deletingIds.has(attachment.id) ? faSpinner : faTrash}
                      className={`w-3 h-3 ${deletingIds.has(attachment.id) ? 'animate-spin' : ''}`}
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modals */}
      {uploadModalOpen && (
        <UploadVideoAttachmentModal
          videoId={videoId}
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onSuccess={handleUploadSuccess}
          existingAttachments={attachments.length}
        />
      )}

      {editModalOpen && selectedAttachment && (
        <EditVideoAttachmentModal
          attachment={selectedAttachment}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedAttachment(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};
